import { Lock } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  ASSUNTOS, botoesDe, intencaoDe, liberado, motivoBloqueio, renderizarCorpo,
  rotuloCampo, tipoDoCampo, type MensagemAprovada,
} from './mensagensAprovadas';

// A ESCOLHA DA MENSAGEM APROVADA (Douglas 25/09/2026, versão compacta aprovada).
// Um jeito só de escolher, na Nova conversa e no quadro da janela fechada:
// botões pequenos por intenção, etiqueta Uti/Mkt, cadeado no que a Meta ainda
// analisa, campos numa linha e a prévia como o cliente recebe.

export function EscolhaMensagemAprovada({
  mensagens, selecionadaId, onSelecionar, valores, onValor, carregando,
}: {
  mensagens: MensagemAprovada[];
  selecionadaId: string;
  onSelecionar: (t: MensagemAprovada) => void;
  valores: Record<number, string>;
  onValor: (pos: number, valor: string) => void;
  carregando?: boolean;
}) {
  const itens = [...mensagens].sort((a, b) => Number(liberado(b)) - Number(liberado(a)));
  const sel = mensagens.find((t) => t.id === selecionadaId) || null;
  const botoes = sel ? botoesDe(sel) : [];

  if (carregando) return <p className="text-[11px] text-muted-foreground">Carregando as mensagens aprovadas…</p>;
  if (!mensagens.length) {
    return <p className="text-[11px] text-muted-foreground">Nenhuma mensagem aprovada nesta linha ainda. Quando a Meta aprovar, ela aparece aqui.</p>;
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5 border-y border-[#EDE8E0] py-2 dark:border-[#332727]">
        {itens.map((t) => {
          const nome = intencaoDe(t)?.apelido ?? t.nome_exibicao;
          if (!liberado(t)) {
            return (
              <span
                key={t.id}
                title={`${motivoBloqueio(t)}\n\n${t.corpo}`}
                className="inline-flex h-7 cursor-not-allowed items-center gap-1 whitespace-nowrap rounded-md border border-dashed border-border-warm px-2.5 text-[12px] text-muted-foreground"
              >
                <Lock className="h-2.5 w-2.5" /> {nome}
              </span>
            );
          }
          const ativa = t.id === selecionadaId;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => onSelecionar(t)}
              title={t.corpo}
              className={ativa
                ? 'inline-flex h-7 items-center whitespace-nowrap rounded-md border border-transparent bg-[#5B0A0D] px-2.5 text-[12px] font-semibold text-white dark:bg-[#9D2530]'
                : 'inline-flex h-7 items-center whitespace-nowrap rounded-md border border-border-warm bg-card px-2.5 text-[12px] text-foreground transition-colors hover:border-[#5B0A0D]/40 hover:text-[#5B0A0D] dark:hover:text-[#C87484]'}
            >
              {nome}
            </button>
          );
        })}
      </div>

      {sel && (
        <>
          {(sel.variaveis || []).length > 0 && (
            <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
              {(sel.variaveis || []).map((v) => (tipoDoCampo(v.campo) === 'assunto' ? (
                <Select key={v.pos} value={valores[v.pos] || ''} onValueChange={(a) => onValor(v.pos, a)}>
                  <SelectTrigger className="h-8 border-border-warm bg-card text-xs">
                    <span className="mr-1 text-[9px] font-bold uppercase tracking-wider text-muted-foreground">Assunto</span>
                    <SelectValue placeholder="escolha" />
                  </SelectTrigger>
                  <SelectContent>
                    {ASSUNTOS.map((a) => <SelectItem key={a} value={a} className="text-xs">{a}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <label key={v.pos} className="flex h-8 min-w-0 items-center gap-1.5 rounded-md border border-border-warm bg-card px-2">
                  <span className="flex-shrink-0 text-[9px] font-bold uppercase tracking-wider text-muted-foreground">{rotuloCampo(v.campo)}</span>
                  <Input
                    value={valores[v.pos] || ''}
                    onChange={(e) => onValor(v.pos, e.target.value)}
                    className="h-7 min-w-0 flex-1 border-0 bg-transparent px-0 text-xs shadow-none focus-visible:ring-0"
                  />
                </label>
              )))}
            </div>
          )}
          <div className="rounded-lg bg-[#EFE7DE] p-2 dark:bg-[#1F1818]">
            <div className="whitespace-pre-wrap rounded-md bg-card px-2.5 py-1.5 text-[12px] leading-snug text-foreground">
              {renderizarCorpo(sel.corpo || '', valores)}
            </div>
            {botoes.length > 0 && (
              <div className="mt-1 flex flex-wrap gap-1">
                {botoes.map((b) => (
                  <span key={b} className="rounded-md bg-card px-2 py-0.5 text-[11px] text-[#1f7aa8]">{b}</span>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

